/**
 * Recursively converts null values to 0 in API response objects.
 * Used to normalize new metrics API responses that return null
 * for zero values (the old RDO API returned 0).
 */
export function normalizeNullsToZero(data: any): any {
  if (data === null || data === undefined) {
    return 0;
  }
  if (Array.isArray(data)) {
    return data.map(normalizeNullsToZero);
  }
  if (typeof data === 'object') {
    for (const key in data) {
      if (data[key] === null) {
        data[key] = 0;
      } else if (typeof data[key] === 'object') {
        data[key] = normalizeNullsToZero(data[key]);
      }
    }
  }
  return data;
}

const PERCENT_FIELD_PREFIXES = [
  'PhysicalUtilization',
  'DollarUtilization',
  'ClientUnavailable',
  'RateChangeMoM',
  'RateChangeYoY',
  'GrowthMoMDiF',
  'GrowthMoMDoR',
  'GrowthYoYDiF',
  'GrowthYoYDoR',
];

function isPercentField(key: string): boolean {
  return PERCENT_FIELD_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Selectively converts null→0 only for percentage/utilization fields.
 * Revenue and dollar fields keep null so they render as dashes in the grid.
 */
export function normalizePercentNullsToZero(data: any): any {
  if (Array.isArray(data)) {
    return data.map(normalizePercentNullsToZero);
  }
  if (data !== null && data !== undefined && typeof data === 'object') {
    for (const key in data) {
      if (typeof data[key] === 'object' && data[key] !== null) {
        data[key] = normalizePercentNullsToZero(data[key]);
      } else if (data[key] === null && isPercentField(key)) {
        data[key] = 0;
      }
    }
  }
  return data;
}

function normalizeEquipmentRow(row: any): any {
  if (row === null || row === undefined || typeof row !== 'object') {
    return row;
  }

  const hasUnitsTotal = row.UnitCountTotal != null && row.UnitCountTotal > 0;
  const hasUnitsBenchmarked =
    row.UnitCountBenchmarked != null && row.UnitCountBenchmarked > 0;

  for (const key in row) {
    if (row[key] !== null || !isPercentField(key)) {
      continue;
    }

    if (key.endsWith('Bench') || key.endsWith('Difference')) {
      // Industry benchmark or server-computed difference — never normalize
      continue;
    }

    if (key.endsWith('Benchmarked')) {
      if (hasUnitsBenchmarked) {
        row[key] = 0;
      }
    } else {
      // Base or *Total suffix
      if (hasUnitsTotal) {
        row[key] = 0;
      }
    }
  }

  return row;
}

/**
 * Row-context-aware null normalization for equipment product type data.
 *
 * Uses UnitCountTotal and UnitCountBenchmarked per row to decide whether
 * null percent fields represent "0% utilization" vs "no data available":
 * - *Total / base fields: normalize only when UnitCountTotal > 0
 * - *Benchmarked fields: normalize only when UnitCountBenchmarked > 0
 * - *Bench / *Difference fields: never normalize (industry data / computed)
 */
export function normalizeEquipmentPercentNulls(data: any): any {
  if (Array.isArray(data)) {
    return data.map((row: any) => normalizeEquipmentRow(row));
  }
  if (data !== null && data !== undefined && typeof data === 'object') {
    if (Array.isArray(data.Items)) {
      data.Items = data.Items.map((row: any) => normalizeEquipmentRow(row));
    }
  }
  return data;
}
